// Interview Request ****************************************************************************
// pathname: /v2/resume-operation/job/{jobSlug}/user/{userSlug}/request-interview
// ************************************************************************************************
import { useSwrMutationApi } from '@hooks/useSwrApi';
import { useMemo } from 'react';

interface UseSwrInterviewRequestProps {
  locale?: string;
  jobSlug: string;
  userSlug: string;
  onSuccess?: (response: unknown) => void;
  onError?: (error: unknown) => void;
}

/**
 * @description SWR mutation: request interview for an application
 *
 * @example
 * const { trigger, isMutating } = useSwrInterviewRequest({
 *   locale: 'ro',
 *   jobSlug: 'job-slug',
 *   userSlug: 'user-slug',
 *   onSuccess: (response) => console.log('Interview requested'),
 *   onError: (error) => console.error('Failed to request interview')
 * });
 *
 * // Trigger the request
 * void trigger();
 */
export const useSwrInterviewRequest = (props: UseSwrInterviewRequestProps) => {
  const {
    locale = 'ro', jobSlug, userSlug, onSuccess,
  } = props;

  // Build the URL with path parameters
  const url = useMemo(
    () => `/v2/resume-operation/job/${jobSlug}/user/${userSlug}/request-interview`,
    [jobSlug, userSlug],
  );

  const {
    data,
    error,
    trigger,
    isMutating,
    reset,
  } = useSwrMutationApi<unknown, undefined>({
    url,
    locale,
    fetcherConfig: {
      method: 'POST',
      stringifyPayload: true,
      expectEmptyResponse: true,
      headers: {
        'Content-Type': 'application/json',
      },
    },
    apiOptions: {
      onSuccess: (response) => {
        if (onSuccess) {
          onSuccess(response);
        }
      },
    },
    toastsOptions: {
      showErrorToast: true,
    },
  });

  return {
    data,
    error,
    trigger,
    isMutating,
    reset,
  };
};
